// src/hooks/usePreviewEvent.js
import { useState, useCallback, useEffect } from 'react';

/**
 * Hook for managing preview event during creation mode
 * 
 * The preview event:
 * - Auto-syncs with selectedDateTime from form
 * - Updates when user types in creation panel
 * - Gets rendered with special preview styling
 * - No manual positioning needed
 * 
 * @param {string} selectedDateTime - Current selected datetime from form (ISO string)
 * @param {boolean} isInCreationMode - Whether we're in creation mode
 * @returns {Object} Preview event state and controls
 */
export const usePreviewEvent = (selectedDateTime = '', isInCreationMode = false) => {
  const [previewEvent, setPreviewEvent] = useState(null);

  /**
   * Auto-sync preview event with form datetime
   * This runs whenever selectedDateTime or creation mode changes
   */
  useEffect(() => {
    if (!isInCreationMode || !selectedDateTime) {
      // Clear preview if not in creation mode or missing datetime
      setPreviewEvent(null);
      return;
    }

    console.log('🎯 Auto-syncing preview event to:', selectedDateTime);
    
    // Super simple - no parsing needed!
    const startTime = new Date(selectedDateTime);
    
    // Validate the date
    if (isNaN(startTime.getTime())) {
      console.error('Invalid selectedDateTime:', selectedDateTime);
      setPreviewEvent(null);
      return;
    }
    
    // Default duration is 3 hours (180 minutes)
    const duration = 180;
    const endTime = new Date(startTime.getTime() + (duration * 60 * 1000));
    
    console.log('🎯 Created preview event:', {
      selectedDateTime,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString()
    });
    
    const newPreviewEvent = {
      id: 'preview-auto-sync',
      title: 'New Event',
      start: startTime,
      end: endTime,
      isPreview: true,
      event_category: 'general',
      backgroundColor: '#3b82f6', // blue-500
      borderColor: '#1d4ed8', // blue-700
      textColor: '#ffffff',
      
      // Default form fields
      description: '',
      duration: duration,
      customerName: '',
      serviceType: '',
      
      // Preview-specific properties
      opacity: 0.7,
      isDragging: false
    };
    
    setPreviewEvent(newPreviewEvent);
  }, [selectedDateTime, isInCreationMode]);

  /**
   * Update preview event from form data
   * Called when user types in the event creation panel
   * @param {Object} formData - Form data from EventCreationPanel
   */
  const updatePreviewFromForm = useCallback((formData) => {
    if (!previewEvent) return;
    
    console.log('📝 Updating preview from form:', formData.title);
    
    // Calculate new end time if duration changed
    const duration = formData.duration || 180;
    const newEndTime = new Date(previewEvent.start.getTime() + (duration * 60 * 1000));
    
    // Color based on event type
    let backgroundColor = '#3b82f6'; // blue-500 (general)
    let borderColor = '#1d4ed8'; // blue-700
    
    if (formData.eventType === 'service') {
      backgroundColor = '#10b981'; // emerald-500
      borderColor = '#047857'; // emerald-700
    } else if (formData.eventType === 'partner_service') {
      backgroundColor = '#8b5cf6'; // violet-500
      borderColor = '#7c3aed'; // violet-600
    }
    
    setPreviewEvent(prev => ({
      ...prev,
      title: formData.title || 'New Event',
      description: formData.description || '',
      event_category: formData.eventType || 'general',
      duration: duration,
      end: newEndTime,
      backgroundColor,
      borderColor,
      
      // Service event fields
      customerName: formData.customerName || '',
      customerEmail: formData.customerEmail || '',
      serviceType: formData.serviceType || '',
      serviceAddress: formData.serviceAddress || '',
      
      // Partner service fields  
      partnerId: formData.partnerId || '',
      serviceId: formData.serviceId || ''
    }));
  }, [previewEvent]);

  /**
   * Update preview event date/time from form
   * This is now handled automatically by the useEffect above
   * Keeping for backward compatibility but it does nothing
   * @deprecated Use selectedDateTime prop instead
   */
  const updatePreviewDateTime = useCallback((date, time) => {
    // This is now handled automatically by useEffect
    console.log('📅 updatePreviewDateTime called (now auto-synced):', date, time);
  }, []);

  /**
   * Clear preview event
   * Called when exiting creation mode
   */
  const clearPreview = useCallback(() => {
    console.log('🧹 Clearing preview event');
    setPreviewEvent(null);
  }, []);

  /**
   * Get current preview event data for form
   * @returns {Object|null} Preview event data or null
   */
  const getPreviewData = useCallback(() => {
    if (!previewEvent) return null;
    
    return {
      dateTime: previewEvent.start.toISOString(),
      duration: previewEvent.duration,
      title: previewEvent.title,
      description: previewEvent.description,
      eventType: previewEvent.event_category
    };
  }, [previewEvent]);

  return {
    // State
    previewEvent,
    
    // Actions
    updatePreviewFromForm,
    updatePreviewDateTime, // Deprecated but kept for compatibility
    clearPreview,
    
    // Utilities
    getPreviewData
  };
};

export default usePreviewEvent;